import React, { useState, useEffect, useMemo } from 'react';
import PageLoader from '../components/PageLoader';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, Check, Trophy, ArrowUpDown, Gift, PartyPopper } from 'lucide-react';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import RemoveEmployeeModal from '../components/RemoveEmployeeModal';
import KudosModal from '../components/KudosModal';
import '../styles/employees.css';

interface Employee {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  status: string;
  createdAt: string;
  points?: number;
}

interface RewardSummary {
  rewardId: string;
  title: string;
  points: number;
  isActive: boolean;
}

type PointsSort = 'none' | 'desc' | 'asc';

type SubmissionStats = {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
};

type RewardProgress =
  | { canClaim: true; reward: RewardSummary }
  | { canClaim: false; progressPercentage: number; pointsToGoal: number };

export default function EmployeesPage() {
  const { token } = useAuth();
  const pointsEnabled = usePointsEnabled();
  const [submissionStats, setSubmissionStats] = useState<Record<string, SubmissionStats>>({});
  const [employeeToRemove, setEmployeeToRemove] = useState<Employee | null>(null);
  const [showKudosModal, setShowKudosModal] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeSuccess, setRemoveSuccess] = useState(false);
  const [pointsSort, setPointsSort] = useState<PointsSort>('none');
  const navigate = useNavigate();

  const {
    items: fetchedEmployees,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  } = usePaginatedFetch<Employee>({
    url: `${authConfig.apiUrl}/api/admin/employees`,
    token,
    dataKey: 'employees',
    // 100 is the server's max page size (see parseLimit in pagination.ts).
    limit: 100,
  });

  // Bonus points from kudos sent on this page, added locally. Refetching the
  // list would swap the page for its loader and unmount the open kudos modal.
  const [kudosPoints, setKudosPoints] = useState<Record<string, number>>({});
  const employees = useMemo(
    () => fetchedEmployees.map((e) =>
      kudosPoints[e.userId] ? { ...e, points: (e.points ?? 0) + kudosPoints[e.userId] } : e
    ),
    [fetchedEmployees, kudosPoints]
  );

  // Auto-page through the full roster so sorting by points reflects every
  // employee, not just the first page loaded.
  useEffect(() => {
    if (hasMore && !loading && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loading, loadingMore, loadMore]);

  const {
    items: rewardsCatalog,
    hasMore: rewardsHasMore,
    loading: rewardsLoading,
    loadingMore: rewardsLoadingMore,
    loadMore: loadMoreRewards,
  } = usePaginatedFetch<RewardSummary>({
    url: `${authConfig.apiUrl}/api/admin/rewards`,
    token,
    dataKey: 'rewards',
    limit: 100,
    enabled: pointsEnabled,
  });

  useEffect(() => {
    if (pointsEnabled && rewardsHasMore && !rewardsLoading && !rewardsLoadingMore) {
      loadMoreRewards();
    }
  }, [pointsEnabled, rewardsHasMore, rewardsLoading, rewardsLoadingMore, loadMoreRewards]);

  const activeRewards = useMemo(
    () => rewardsCatalog.filter((r) => r.isActive),
    [rewardsCatalog]
  );

  const lowestRewardGoal = useMemo(() => {
    if (activeRewards.length === 0) return null;
    return Math.min(...activeRewards.map((r) => r.points || 0));
  }, [activeRewards]);

  // Progress toward the cheapest reward, or the best (highest-value) reward
  // an employee can currently afford once they've crossed that threshold.
  const getRewardProgress = (points: number): RewardProgress | null => {
    if (lowestRewardGoal === null) return null;
    if (points >= lowestRewardGoal) {
      const claimable = activeRewards.filter((r) => (r.points || 0) <= points);
      const best = claimable.reduce((max, r) => ((r.points || 0) > (max.points || 0) ? r : max));
      return { canClaim: true, reward: best };
    }
    return {
      canClaim: false,
      progressPercentage: lowestRewardGoal > 0 ? (points / lowestRewardGoal) * 100 : 0,
      pointsToGoal: lowestRewardGoal - points,
    };
  };

  const sortedEmployees = useMemo(() => {
    if (pointsSort === 'none') return employees;
    const withPoints = [...employees];
    withPoints.sort((a, b) => {
      const diff = (a.points || 0) - (b.points || 0);
      return pointsSort === 'asc' ? diff : -diff;
    });
    return withPoints;
  }, [employees, pointsSort]);

  const cyclePointsSort = () => {
    setPointsSort((prev) => (prev === 'none' ? 'desc' : prev === 'desc' ? 'asc' : 'none'));
  };

  // Fetch submission stats for any employees not yet in the cache
  useEffect(() => {
    if (!token || employees.length === 0) return;
    const missing = employees.filter((e) => !submissionStats[e.userId]);
    if (missing.length === 0) return;

    fetch(`${authConfig.apiUrl}/api/admin/submissions/user-stats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userIds: missing.map((e) => e.userId) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.stats) setSubmissionStats((prev) => ({ ...prev, ...data.stats }));
      })
      .catch(() => {});
  }, [employees]);

  const handleRemove = async () => {
    if (!employeeToRemove || !token) return;

    setRemoving(true);
    setRemoveError(null);

    try {
      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/employees/${employeeToRemove.userId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to remove employee');
      }

      setRemoving(false);
      setRemoveSuccess(true);
      setTimeout(() => {
        setEmployeeToRemove(null);
        setRemoveSuccess(false);
        refresh();
      }, 1300);
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove employee');
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="employees-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="employees-container">
        <div className="employees-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="employees-container">
      <div className="employees-header employees-header-row">
        <div>
          <h1 className="employees-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={28} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
            Team Members
          </h1>
          <p className="employees-subtitle">
            {employees.length} {employees.length === 1 ? 'employee' : 'employees'} in your company
          </p>
        </div>
        {employees.some((e) => e.status === 'active') && (
          <button type="button" className="employees-kudos-btn" onClick={() => setShowKudosModal(true)}>
            <PartyPopper size={18} /> Send Kudos
          </button>
        )}
      </div>

      {showKudosModal && (
        <KudosModal
          source="employees-page"
          onClose={() => setShowKudosModal(false)}
          onSent={(r, recipient) => {
            if (r.pointsAwarded > 0) {
              setKudosPoints((prev) => ({ ...prev, [recipient.userId]: (prev[recipient.userId] ?? 0) + r.pointsAwarded }));
            }
          }}
        />
      )}

      {pointsEnabled && employees.length > 0 && (
        <div className="employees-toolbar">
          <button
            type="button"
            className={`employees-sort-btn${pointsSort !== 'none' ? ' active' : ''}`}
            onClick={cyclePointsSort}
          >
            <ArrowUpDown size={14} />
            Point Total
            {pointsSort === 'desc' && ' (High to Low)'}
            {pointsSort === 'asc' && ' (Low to High)'}
          </button>
        </div>
      )}

      {employees.length === 0 ? (
        <div className="employees-empty">
          <div className="employees-empty-icon"><Users size={48} /></div>
          <h2 className="employees-empty-title">No employees yet</h2>
          <p className="employees-empty-text">
            Invite team members to get started
          </p>
        </div>
      ) : (
        <div className="employees-grid">
          {sortedEmployees.map((employee) => (
            <div key={employee.userId} className="employee-card">
              <div className="employee-avatar">
                {employee.profilePicture ? (
                  <img 
                    src={employee.profilePicture} 
                    alt={`${employee.firstName} ${employee.lastName}`}
                    className="employee-avatar-img"
                  />
                ) : (
                  <div className="employee-avatar-placeholder">
                    {employee.firstName?.[0]?.toUpperCase() || '?'}
                    {employee.lastName?.[0]?.toUpperCase() || ''}
                  </div>
                )}
              </div>
              
              <div className="employee-info">
                <h3 className="employee-name">
                  {employee.firstName || 'Unknown'} {employee.lastName || 'User'}
                </h3>
                <p className="employee-email">{employee.email}</p>
              </div>

              <div className="employee-stats">
                {(() => {
                  const stats = submissionStats[employee.userId];
                  const submissionsTotal = stats?.total ?? 0;
                  const approved = stats?.approved ?? 0;
                  const pending = stats?.pending ?? 0;
                  const rejected = stats?.rejected ?? 0;

                  return (
                    <>
                      <div className="employee-submissions-feature">
                        <div className="employee-submissions-feature-value">{submissionsTotal}</div>
                        <div className="employee-submissions-feature-label">Submissions</div>
                        <div className="employee-submissions-feature-meta">
                          Approved: {approved} · Pending: {pending} · Rejected: {rejected}
                        </div>
                      </div>
                      {pointsEnabled && (() => {
                        const progress = getRewardProgress(employee.points || 0);
                        return (
                          <div className="employee-points-feature">
                            <div className="employee-points-feature-value">
                              <Trophy size={18} />
                              {(employee.points || 0).toLocaleString()}
                            </div>
                            <div className="employee-points-feature-label">Point Total</div>
                            {progress && (
                              progress.canClaim ? (
                                <div className="employee-reward-indicator claimable">
                                  <Gift size={12} />
                                  Can claim: {progress.reward.title}
                                </div>
                              ) : (
                                <div className="employee-reward-indicator">
                                  <div className="employee-reward-progress-bar">
                                    <div
                                      className="employee-reward-progress-fill"
                                      style={{ width: `${Math.min(progress.progressPercentage, 100)}%` }}
                                    />
                                  </div>
                                  <div className="employee-reward-progress-label">
                                    {progress.pointsToGoal.toLocaleString()} pts to next reward
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>

              <div className="employee-actions">
                <button
                  className="employee-view-btn"
                  onClick={() => navigate(`/employees/${employee.userId}`)}
                >
                  View Profile
                </button>
                <button
                  className="employee-remove-btn"
                  onClick={() => {
                    setRemoveError(null);
                    setEmployeeToRemove(employee);
                  }}
                >
                  Remove
                </button>
              </div>

              <div className="employee-status">
                <span className={`employee-status-badge ${employee.status}`}>
                  {employee.status === 'invited' ? <><Mail size={14} /> Invited</> : <><Check size={14} /> Active</>}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {(loadingMore || hasMore) && (
        <p className="employees-loading-more">Loading more employees…</p>
      )}

      {employeeToRemove && (
        <RemoveEmployeeModal
          name={
            `${employeeToRemove.firstName || ''} ${employeeToRemove.lastName || ''}`.trim() ||
            employeeToRemove.email
          }
          status={employeeToRemove.status}
          loading={removing}
          success={removeSuccess}
          error={removeError}
          onConfirm={handleRemove}
          onClose={() => setEmployeeToRemove(null)}
        />
      )}
    </div>
  );
}

